import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { dashboardService } from '../services';
import { useAuthStore } from '../store/authStore';
import { useT } from '../hooks/useT';

function StatCard({ label, value, sub }) {
  return (
    <div className="card">
      <p className="text-xs text-slate-500 mb-1">{label}</p>
      <p className="text-3xl font-bold text-slate-100">{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
    </div>
  );
}

function GoalProgress({ goal, currency = 'USD' }) {
  const t = useT();
  const locale = currency === 'BRL' ? 'pt-BR' : currency === 'EUR' ? 'de-DE' : 'en-US';
  const pct = Math.min(100, parseFloat(goal.progress_percent) || 0);
  return (
    <div className="card hover:border-slate-600 transition-colors">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="font-semibold text-slate-200">{goal.title}</h3>
          <p className="text-xs text-slate-500">
            {goal.plan_id ? t('dashboard.goals.months_remaining').replace('{n}', goal.months_remaining || '?') : t('dashboard.goals.no_plan')}
          </p>
        </div>
        <span className="text-2xl font-bold text-slate-100">{pct.toFixed(0)}%</span>
      </div>
      <div className="w-full h-2 bg-surface rounded-full overflow-hidden">
        <div
          className="h-full bg-brand-500 rounded-full transition-all duration-700"
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="flex items-center justify-between mt-3">
        <span className="text-xs text-slate-500">
          {parseFloat(goal.amount_saved || 0).toLocaleString(locale, { style: 'currency', currency })} {t('dashboard.goals.saved')}
        </span>
        <span className="text-xs text-slate-500">
          {t('common.of')} {parseFloat(goal.effective_cost || 0).toLocaleString(locale, { style: 'currency', currency })}
        </span>
      </div>
      {goal.plan_id && (
        <Link
          to={`/plans/${goal.plan_id}`}
          className="block mt-3 text-xs text-brand-400 hover:text-brand-300 transition-colors"
        >
          {t('dashboard.goals.view_plan')}
        </Link>
      )}
    </div>
  );
}

const DEBT_TYPE_LABELS = {
  credit_card:    '💳',
  personal_loan:  '🏦',
  student_loan:   '🎓',
  car_loan:       '🚗',
  mortgage:       '🏠',
  medical:        '🏥',
  other:          '📋',
};

function DebtCard({ debt, currency = 'USD' }) {
  const t = useT();
  const locale = currency === 'BRL' ? 'pt-BR' : currency === 'EUR' ? 'de-DE' : 'en-US';
  const icon = DEBT_TYPE_LABELS[debt.debt_type] || '📋';
  return (
    <div className="card hover:border-slate-600 transition-colors">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-xl">{icon}</span>
          <div>
            <h3 className="font-semibold text-slate-200">{debt.title}</h3>
            <p className="text-xs text-slate-500">
              {debt.plan_id
                ? t('dashboard.debts.months_to_payoff').replace('{n}', debt.months_to_payoff ?? '?')
                : t('dashboard.debts.no_plan')}
            </p>
          </div>
        </div>
        {debt.interest_rate > 0 && (
          <span className="text-xs text-slate-400 shrink-0">
            {t('dashboard.debts.interest_rate').replace('{n}', parseFloat(debt.interest_rate).toFixed(1))}
          </span>
        )}
      </div>
      <div className="flex items-center justify-between mt-1">
        <div>
          <p className="text-xs text-slate-500 mb-0.5">{t('dashboard.debts.monthly_payment')}</p>
          <p className="text-lg font-bold text-slate-100">
            {debt.plan_id
              ? parseFloat(debt.monthly_payment || 0).toLocaleString(locale, { style: 'currency', currency })
              : parseFloat(debt.minimum_payment || 0).toLocaleString(locale, { style: 'currency', currency })}
          </p>
        </div>
        <div className="text-right">
          <p className="text-xs text-slate-500 mb-0.5">{t('common.total')}</p>
          <p className="text-sm font-semibold text-slate-300">
            {parseFloat(debt.total_debt || 0).toLocaleString(locale, { style: 'currency', currency })}
          </p>
        </div>
      </div>
      {debt.plan_id && (
        <Link
          to={`/debt/${debt.debt_goal_id}/plan`}
          className="block mt-3 text-xs text-brand-400 hover:text-brand-300 transition-colors"
        >
          {t('dashboard.debts.view_plan')}
        </Link>
      )}
    </div>
  );
}

export default function DashboardPage() {
  const { user } = useAuthStore();
  const t = useT();
  const currency = user?.currency || 'USD';
  const currencyLocale = currency === 'BRL' ? 'pt-BR' : currency === 'EUR' ? 'de-DE' : 'en-US';
  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboard'],
    queryFn: dashboardService.get,
  });

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-surface-card rounded-xl w-64" />
        <div className="grid grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => <div key={i} className="h-28 bg-surface-card rounded-2xl" />)}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card text-red-400">
        {t('dashboard.error')}
      </div>
    );
  }

  const { summary, goals, debts } = data || { summary: {}, goals: [], debts: [] };

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-100">
          {t(`dashboard.greeting.${getTimeOfDay()}`)}, {user?.full_name?.split(' ')[0]} 👋
        </h1>
        <p className="text-slate-400 mt-1">{t('dashboard.subtitle')}</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard
          label={t('dashboard.stat.active_goals')}
          value={summary.active_goals ?? 0}
          sub={t('dashboard.stat.active_goals.sub')}
        />
        <StatCard
          label={t('dashboard.stat.total_saved')}
          value={parseFloat(summary.total_saved || 0).toLocaleString(currencyLocale, { style: 'currency', currency, maximumFractionDigits: 0 })}
          sub={t('dashboard.stat.total_saved.sub')}
        />
        <StatCard
          label={t('dashboard.stat.monthly_commitment')}
          value={parseFloat(summary.monthly_commitment || 0).toLocaleString(currencyLocale, { style: 'currency', currency, maximumFractionDigits: 0 })}
          sub={t('dashboard.stat.monthly_commitment.sub')}
        />
        <StatCard
          label={t('dashboard.stat.active_plans')}
          value={summary.active_plans ?? 0}
          sub={t('dashboard.stat.active_plans.sub')}
        />
        <StatCard
          label={t('dashboard.stat.active_debts')}
          value={summary.active_debts ?? 0}
          sub={t('dashboard.stat.active_debts.sub')}
        />
      </div>

      {/* Goal progress */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-slate-200">{t('dashboard.goals.title')}</h2>
          <Link to="/goals" className="text-sm text-brand-400 hover:text-brand-300 transition-colors">
            {t('dashboard.goals.manage')}
          </Link>
        </div>

        {goals.length === 0 ? (
          <div className="card text-center py-12">
            <p className="text-4xl mb-4">🎯</p>
            <p className="text-slate-300 font-medium">{t('dashboard.goals.empty.title')}</p>
            <p className="text-slate-500 text-sm mt-1 mb-6">{t('dashboard.goals.empty.sub')}</p>
            <Link to="/goals" className="btn-primary inline-block">
              {t('dashboard.goals.empty.cta')}
            </Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {goals.map((goal) => (
              <GoalProgress key={goal.goal_id} goal={goal} currency={currency} />
            ))}
          </div>
        )}
      </div>

      {/* Debt section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-slate-200">{t('dashboard.debts.title')}</h2>
          <Link to="/debt" className="text-sm text-brand-400 hover:text-brand-300 transition-colors">
            {t('dashboard.debts.manage')}
          </Link>
        </div>

        {debts.length === 0 ? (
          <div className="card text-center py-12">
            <p className="text-4xl mb-4">💳</p>
            <p className="text-slate-300 font-medium">{t('dashboard.debts.empty.title')}</p>
            <p className="text-slate-500 text-sm mt-1 mb-6">{t('dashboard.debts.empty.sub')}</p>
            <Link to="/debt" className="btn-primary inline-block">
              {t('dashboard.debts.empty.cta')}
            </Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {debts.map((debt) => (
              <DebtCard key={debt.debt_goal_id} debt={debt} currency={currency} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function getTimeOfDay() {
  const h = new Date().getHours();
  if (h < 12) return 'morning';
  if (h < 18) return 'afternoon';
  return 'evening';
}
